/**
 * Backend streaming STT adapter (#93, D-STT / D-PRIV).
 *
 * `TranscribeRecognizer` implements the same {@link SpeechRecognizer} seam as the
 * browser {@link WebSpeechRecognizer}, so the answer-capture hook is unchanged
 * (D-SEAM). On `start`, it opens the `/api/voice/stt` WebSocket (with the game ticket
 * for auth), captures the mic as 16-bit PCM 16 kHz via Web Audio, and streams the
 * frames as binary. The server proxies a Transcribe streaming session and relays text
 * JSON back: `partial` → `onResult({ isFinal: false })`, `final` → `onEnd(transcript)`,
 * `error` → `onError`. `stop()` sends `{"type":"stop"}` to flush; `abort()` tears down
 * without firing `onEnd`.
 *
 * The socket and the mic-capture (PCM source) layers are both injectable so unit tests
 * drive the mapping/protocol without real Web Audio or `getUserMedia`. The raw DSP
 * (Float32 → Int16 LE, optional downsample) lives in `pcm.ts`, tested in isolation.
 *
 * D-PRIV: audio bytes are streamed to the in-VPC session only and are never persisted
 * or logged here; the recognizer forwards frames and drops them.
 */

import { wsBaseUrl } from '../config';
import { getVoiceTicket } from './voice-config';
import type { RecognitionHandlers, SpeechRecognizer } from './speech-recognition';
import { encodePcmFrame } from './pcm';

/**
 * A microphone capture that yields Int16 PCM (mono, 16 kHz) frames. The default impl
 * uses Web Audio; tests inject a fake that pushes canned frames.
 */
export interface PcmSource {
  /** Begin capture; each frame arrives via `onFrame`. Rejects if the mic is denied. */
  start(onFrame: (frame: Int16Array) => void): Promise<void>;
  /** Stop capture and release the mic. */
  stop(): void;
}

export type PcmSourceFactory = () => PcmSource;

/** Structural subset of the STT WebSocket this adapter drives. */
export interface SttSocketLike {
  send(data: ArrayBufferView | ArrayBuffer | string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
  onclose: (() => void) | null;
  readonly readyState: number;
}

export type SttSocketFactory = (url: string) => SttSocketLike;

export interface TranscribeRecognizerOptions {
  /** WebSocket constructor. Defaults to the browser `WebSocket`. Injectable for tests. */
  socketFactory?: SttSocketFactory;
  /** Mic-capture source. Defaults to a Web Audio PCM source. Injectable for tests. */
  pcmSourceFactory?: PcmSourceFactory;
  /** Supplies the game ticket for auth. Defaults to {@link getVoiceTicket}. */
  ticketProvider?: () => string | null;
  /** WS base (`ws(s)://…`). Defaults to {@link wsBaseUrl}. */
  wsBase?: string;
  /**
   * The browser recognizer used when the backend session fails (D-FALL middle tier:
   * backend → browser → typed). On any setup/socket error, `start` is delegated to
   * this fallback when it `isSupported()`; otherwise `onError` fires and the hook
   * shows the typed fallback. Optional — omitted (tests / no browser STT) skips
   * straight to the typed path.
   */
  fallback?: SpeechRecognizer;
}

const WS_OPEN = 1;

interface SttMessage {
  type?: string;
  transcript?: string;
  message?: string;
}

/** Backend {@link SpeechRecognizer} over `WS /api/voice/stt` (Transcribe streaming). */
export class TranscribeRecognizer implements SpeechRecognizer {
  private readonly socketFactory: SttSocketFactory;
  private readonly pcmSourceFactory: PcmSourceFactory;
  private readonly ticketProvider: () => string | null;
  private readonly wsBase: string;
  private readonly fallback: SpeechRecognizer | null;

  private socket: SttSocketLike | null = null;
  private source: PcmSource | null = null;
  private handlers: RecognitionHandlers | null = null;
  /** True once a terminal callback (onEnd/onError) has fired — suppresses duplicates. */
  private ended = false;
  private latestTranscript = '';
  /** The browser recognizer we handed off to after a backend failure, if any. */
  private delegate: SpeechRecognizer | null = null;

  constructor(opts: TranscribeRecognizerOptions = {}) {
    this.socketFactory = opts.socketFactory ?? defaultSocketFactory;
    this.pcmSourceFactory = opts.pcmSourceFactory ?? (() => new WebAudioPcmSource());
    this.ticketProvider = opts.ticketProvider ?? getVoiceTicket;
    this.wsBase = opts.wsBase ?? wsBaseUrl();
    this.fallback = opts.fallback ?? null;
  }

  /** Backend STT is always considered available; failures surface via `onError`. */
  isSupported(): boolean {
    return true;
  }

  start(handlers: RecognitionHandlers): void {
    // Drop any prior session so a restart never double-streams.
    this.teardown();
    this.ended = false;
    this.latestTranscript = '';
    this.delegate = null;
    this.handlers = handlers;

    const ticket = this.ticketProvider();
    const url = `${this.wsBase}/api/voice/stt${
      ticket ? `?ticket=${encodeURIComponent(ticket)}` : ''
    }`;

    let socket: SttSocketLike;
    try {
      socket = this.socketFactory(url);
    } catch {
      this.fail('stt-socket-failed');
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      void this.beginCapture();
    };
    socket.onmessage = (ev) => this.handleMessage(ev.data);
    socket.onerror = () => this.fail('stt-socket-error');
    socket.onclose = () => {
      // Server-side close without a final → tear the mic down (no onEnd — the hook's
      // answer-window timer bounds the turn).
      this.stopCapture();
    };
  }

  private async beginCapture(): Promise<void> {
    try {
      const source = this.pcmSourceFactory();
      this.source = source;
      await source.start((frame) => {
        const socket = this.socket;
        if (this.ended || !socket || socket.readyState !== WS_OPEN) return;
        try {
          socket.send(frame.buffer as ArrayBuffer);
        } catch {
          /* a dropped frame is harmless; the stream continues */
        }
      });
    } catch {
      this.fail('mic-unavailable');
    }
  }

  private handleMessage(data: unknown): void {
    if (this.ended || !this.handlers) return;
    let msg: SttMessage;
    try {
      msg = JSON.parse(String(data)) as SttMessage;
    } catch {
      return; // ignore non-JSON frames
    }
    switch (msg.type) {
      case 'partial': {
        const transcript = msg.transcript ?? '';
        this.latestTranscript = transcript;
        this.handlers.onResult({ transcript, isFinal: false });
        break;
      }
      case 'final':
        this.latestTranscript = msg.transcript ?? this.latestTranscript;
        this.emitEnd(this.latestTranscript);
        break;
      case 'error':
        this.fail(msg.message ?? 'stt-error');
        break;
      default:
        break;
    }
  }

  stop(): void {
    // If we handed off to the browser fallback, forward there.
    if (this.delegate) {
      this.delegate.stop();
      return;
    }
    // Flush: stop the mic, ask the server for the final transcript. The `final`
    // message then drives `onEnd`. If the socket is already gone, end with whatever
    // we have so the caller is never left hanging.
    this.stopCapture();
    const socket = this.socket;
    if (socket && socket.readyState === WS_OPEN) {
      try {
        socket.send(JSON.stringify({ type: 'stop' }));
      } catch {
        this.emitEnd(this.latestTranscript);
      }
    } else {
      this.emitEnd(this.latestTranscript);
    }
  }

  abort(): void {
    // Teardown WITHOUT onEnd (used on unmount / turn change).
    this.ended = true;
    if (this.delegate) {
      const delegate = this.delegate;
      this.delegate = null;
      delegate.abort();
      return;
    }
    this.teardown();
  }

  private emitEnd(transcript: string): void {
    if (this.ended) return;
    this.ended = true;
    const handlers = this.handlers;
    this.teardown();
    handlers?.onEnd(transcript);
  }

  private fail(error: string): void {
    if (this.ended) return;
    this.ended = true;
    const handlers = this.handlers;
    this.teardown();
    if (!handlers) return;
    // D-FALL middle tier: a backend setup/socket error hands off to the browser
    // recognizer where supported; only when that too is unavailable do we surface
    // `onError` (→ the hook's typed fallback).
    if (this.fallback && this.fallback.isSupported()) {
      this.delegate = this.fallback;
      this.fallback.start(handlers);
      return;
    }
    handlers.onError?.(error);
  }

  private stopCapture(): void {
    const source = this.source;
    this.source = null;
    if (source) {
      try {
        source.stop();
      } catch {
        /* ignore */
      }
    }
  }

  private teardown(): void {
    this.stopCapture();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        /* ignore */
      }
    }
  }
}

function defaultSocketFactory(url: string): SttSocketLike {
  return new WebSocket(url) as unknown as SttSocketLike;
}

// --- Default Web Audio mic capture (browser-only; not exercised under jsdom). ---

/** Structural subset of the Web Audio / media globals the default source touches. */
interface MediaStreamLike {
  getTracks(): Array<{ stop(): void }>;
}
interface AudioNodeLike {
  connect(dest: AudioNodeLike): void;
  disconnect(): void;
}
interface ScriptProcessorLike extends AudioNodeLike {
  onaudioprocess:
    ((ev: { inputBuffer: { getChannelData(ch: number): Float32Array } }) => void) | null;
}
interface AudioContextLike {
  readonly sampleRate: number;
  createMediaStreamSource(stream: MediaStreamLike): AudioNodeLike;
  createScriptProcessor(
    bufferSize: number,
    inCh: number,
    outCh: number,
  ): ScriptProcessorLike;
  readonly destination: AudioNodeLike;
  close(): Promise<void> | void;
}

const TARGET_SAMPLE_RATE = 16000;

/**
 * Default {@link PcmSource}: `getUserMedia({audio})` → `AudioContext({sampleRate:16000})`
 * → a `ScriptProcessorNode` that converts each Float32 frame to Int16 PCM via
 * {@link encodePcmFrame} (downsampling if the context rate differs from 16 kHz). This
 * runs only in a real browser; unit tests inject a fake {@link PcmSource}.
 */
export class WebAudioPcmSource implements PcmSource {
  private stream: MediaStreamLike | null = null;
  private context: AudioContextLike | null = null;
  private processor: ScriptProcessorLike | null = null;
  private sourceNode: AudioNodeLike | null = null;

  async start(onFrame: (frame: Int16Array) => void): Promise<void> {
    const nav = globalThis as unknown as {
      navigator?: {
        mediaDevices?: { getUserMedia(c: unknown): Promise<MediaStreamLike> };
      };
      AudioContext?: new (opts?: { sampleRate?: number }) => AudioContextLike;
      webkitAudioContext?: new (opts?: { sampleRate?: number }) => AudioContextLike;
    };
    const getUserMedia = nav.navigator?.mediaDevices?.getUserMedia;
    const Ctx = nav.AudioContext ?? nav.webkitAudioContext;
    if (!getUserMedia || !Ctx) throw new Error('Web Audio capture unavailable');

    const stream = await getUserMedia.call(nav.navigator!.mediaDevices, { audio: true });
    this.stream = stream;
    const context = new Ctx({ sampleRate: TARGET_SAMPLE_RATE });
    this.context = context;
    const sourceNode = context.createMediaStreamSource(stream);
    this.sourceNode = sourceNode;
    // 4096-sample buffer, mono in/out. ScriptProcessor is deprecated but universally
    // available; an AudioWorklet is the follow-up optimization.
    const processor = context.createScriptProcessor(4096, 1, 1);
    this.processor = processor;
    processor.onaudioprocess = (ev) => {
      const input = ev.inputBuffer.getChannelData(0);
      onFrame(encodePcmFrame(input, context.sampleRate, TARGET_SAMPLE_RATE));
    };
    sourceNode.connect(processor);
    processor.connect(context.destination);
  }

  stop(): void {
    if (this.processor) {
      this.processor.onaudioprocess = null;
      try {
        this.processor.disconnect();
      } catch {
        /* ignore */
      }
      this.processor = null;
    }
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        /* ignore */
      }
      this.sourceNode = null;
    }
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      }
      this.stream = null;
    }
    if (this.context) {
      try {
        void this.context.close();
      } catch {
        /* ignore */
      }
      this.context = null;
    }
  }
}
